import { createClient, type WebDAVClient } from 'webdav'
import { getWorkosAccessToken } from '../models/useAuth'

// The file surface has its own host and its own URL shape (router NF-14):
//
//   https://files.nobgp.com/networks/<network_id>/…
//   https://files.nobgp.com/nodes/<node_id>/…
//
// Full WebDAV — PROPFIND, PUT, MKCOL, MOVE, DELETE — authenticated with the
// same WorkOS bearer token every other call uses.
//
// ⚠ Keyed on the UUID, and that is not cosmetic. The old `/dav/<network-name>/`
// resolved a name SERVER-SIDE, and a name does not identify a network: the same
// name can sit in several of a member's orgs, and the router picked "personal
// org wins, then oldest". Two users could open the same URL and get different
// networks. A UUID names exactly one.
//
// ⚠ The router serves these paths on every host it answers, so the origin is
// overridable for local dev; `files.nobgp.com` is what we ADVERTISE so a client
// never has to guess which spelling is canonical. ⚠ An explicitly EMPTY
// `VITE_FILES_API_URL` means SAME-ORIGIN, and `??` rather than `||` is what
// makes that expressible: vite's dev proxy forwards `/networks` and `/nodes` to
// the router, mirroring `VITE_ROUTER_API_URL`'s dev convention. Unset still
// reaches prod, so a dev app with no `.env.local` entry browses REAL files.
const filesBaseUrl =
  import.meta.env.VITE_FILES_API_URL?.replace(/\/$/, '') ?? 'https://files.nobgp.com'

// Which of the two storage trees a view is looking at. A network's share is
// shared by everyone in the network; a node's area follows the machine and
// belongs to no network at all — which is why it is addressed by node id and
// not by "the node's network".
export type FileTree = 'networks' | 'nodes'

export interface FileTarget {
  tree: FileTree
  id: string // network_id or node_id — a UUID either way
}

export const filesTargetUrl = ({ tree, id }: FileTarget) =>
  `${filesBaseUrl}/${tree}/${encodeURIComponent(id)}`

// A stable string for cache keys and React deps.
export const targetKey = ({ tree, id }: FileTarget) => `${tree}:${id}`

// Creates a fresh WebDAV client bound to a specific network. We build a new
// client per call so the Authorization header reflects the latest WorkOS
// access token (tokens refresh over time).
//
// `remoteBasePath: "/"` is important. The router serves WebDAV under
// `/dav/<network>/...` but its PROPFIND responses contain bare hrefs like
// `/books/` (relative to the network's filesystem root, not to the URL).
// The webdav npm client by default assumes hrefs carry the full URL prefix
// and computes filenames with `pathPosix.relative(base, href)` — which turns
// our bare hrefs into mangled paths like `/../../books` and breaks the
// self-entry filter. Setting remoteBasePath to "/" tells the client to treat
// hrefs as already filesystem-root relative, which matches what the router
// emits.
export function createDavClient(target: FileTarget): WebDAVClient {
  const token = getWorkosAccessToken()
  return createClient(filesTargetUrl(target), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    remoteBasePath: '/',
  })
}

// Build a normalised "/a/b/c" WebDAV path from a list of segments. Empty or
// undefined segments are dropped. Always leads with "/".
export function joinDavPath(...parts: Array<string | undefined | null>): string {
  const segments = parts
    .filter((p): p is string => typeof p === 'string' && p.length > 0)
    .flatMap(p => p.split('/'))
    .filter(s => s.length > 0 && s !== '..' && s !== '.')
  return '/' + segments.join('/')
}

// Normalise a WebDAV path: leading slash, no trailing slash.
// Used to compare paths for self-entry filtering.
export function normaliseDavPath(p: string): string {
  const withSlash = p.startsWith('/') ? p : '/' + p
  return withSlash === '/' ? '/' : withSlash.replace(/\/+$/, '')
}

// Return the parent directory of a WebDAV path (e.g. "/foo/bar.txt" → "/foo",
// "/foo" → "/", "/" → "/").
export function parentDavPath(path: string): string {
  const segs = path.split('/').filter(Boolean)
  segs.pop()
  return segs.length ? '/' + segs.join('/') : '/'
}

// Percent-encode each path segment, preserving "/" separators.
export function encodeDavPath(path: string): string {
  return path.split('/').map(encodeURIComponent).join('/')
}

// PUT a Blob/File to the router's WebDAV endpoint with optional progress
// events. Uses XHR rather than fetch because fetch has no reliable upload
// progress; both stream Blob bodies without buffering into memory.
export function davPut(
  target: FileTarget,
  path: string,
  body: Blob,
  onProgress?: (loaded: number, total: number) => void
): Promise<void> {
  const url = `${filesTargetUrl(target)}${encodeDavPath(path)}`
  const token = getWorkosAccessToken()
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.setRequestHeader('Content-Type', 'application/octet-stream')
    if (onProgress) {
      xhr.upload.onprogress = e => {
        if (e.lengthComputable) onProgress(e.loaded, e.total)
      }
    }
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText || 'error'}`))
    xhr.onerror = () => reject(new Error('Network error during upload'))
    xhr.onabort = () => reject(new Error('Upload aborted'))
    xhr.send(body)
  })
}
